import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Paper,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
} from '@mui/material';
import DocumentComponent from 'components/shared/document_component';
import TextFieldDataCustom from 'components/shared/textfiel_data_custom';

const instrumentTypes = [
  'Lei municipal',
  'Decreto',
  'Portaria',
  'Resolução',
  'Instrução normativa',
  'Outro',
];

export interface Step1CmdrsData {
  hasCmdrs: boolean | null;
  instrument: string;
  instrumentNumber: string;
  publicationDate: string;
  document_uuid: string | null;
  status: string;
  structure: string;
  presidingInstitution: string;
  mandateEnd: string;
  qtdPoderPublico: number;
  qtdSociedadeCivil: number;
}

interface Step1Props {
  onNext: () => void;
  onChange: (data: Step1CmdrsData) => void;
  data: Step1CmdrsData;
}

const Step1: React.FC<Step1Props> = ({
  onNext,
  onChange,
  data,
}) => {
  const [hasCmdrs, setHasCmdrs] = useState<boolean | null>(data.hasCmdrs ?? null);
  const [instrument, setInstrument] = useState(data.instrument ?? '');
  const [instrumentNumber, setInstrumentNumber] = useState(data.instrumentNumber ?? '');
  const [publicationDate, setPublicationDate] = useState(data.publicationDate ?? '');
  const [documentUuid, setDocumentUuid] = useState<string | null>(data.document_uuid ?? null);
  const [status, setStatus] = useState(data.status ?? '');
  const [structure, setStructure] = useState(data.structure ?? '');
  const [presidingInstitution, setPresidingInstitution] = useState(data.presidingInstitution ?? '');
  const [mandateEnd, setMandateEnd] = useState(data.mandateEnd ?? '');
  const [qtdPoderPublico, setQtdPoderPublico] = useState<number>(data.qtdPoderPublico ?? 0);
  const [qtdSociedadeCivil, setQtdSociedadeCivil] = useState<number>(data.qtdSociedadeCivil ?? 0);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Atualiza os estados internos quando o prop data mudar
  useEffect(() => {
    setHasCmdrs(data.hasCmdrs ?? null);
    setInstrument(data.instrument ?? '');
    setInstrumentNumber(data.instrumentNumber ?? '');
    setPublicationDate(data.publicationDate ?? '');
    setDocumentUuid(data.document_uuid ?? null);
    setStatus(data.status ?? '');
    setStructure(data.structure ?? '');
    setPresidingInstitution(data.presidingInstitution ?? '');
    setMandateEnd(data.mandateEnd ?? '');
    setQtdPoderPublico(data.qtdPoderPublico ?? 0);
    setQtdSociedadeCivil(data.qtdSociedadeCivil ?? 0);
  }, [data]);

  // Atualiza o estado global sempre que algum campo mudar
  useEffect(() => {
    onChange({
      hasCmdrs,
      instrument,
      instrumentNumber,
      publicationDate,
      document_uuid: documentUuid,
      status,
      structure,
      presidingInstitution,
      mandateEnd,
      qtdPoderPublico,
      qtdSociedadeCivil,
    });
    // eslint-disable-next-line
  }, [
    hasCmdrs,
    instrument,
    instrumentNumber,
    publicationDate,
    documentUuid,
    status,
    structure,
    presidingInstitution,
    mandateEnd,
    qtdPoderPublico,
    qtdSociedadeCivil,
  ]);

  const handleHasCmdrs = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === 'sim';
    setHasCmdrs(value);
    if (!value) {
      setInstrument('');
      setInstrumentNumber('');
      setPublicationDate('');
      setDocumentUuid(null);
      setStatus('');
      setStructure('');
      setPresidingInstitution('');
      setMandateEnd('');
      setQtdPoderPublico(0);
      setQtdSociedadeCivil(0);
      setErrors({});
    }
  };

  const handleSubmit = () => {
    const newErrors: { [key: string]: string } = {};

    const validateRequiredFields = (fields: { [key: string]: any }) => {
      Object.keys(fields).forEach((field) => {
        if (!fields[field]) {
          newErrors[field] = 'Campo obrigatório';
        }
      });
    };

    const validatePositiveNumber = (fields: { [key: string]: any }) => {
      Object.keys(fields).forEach((field) => {
        if (fields[field] < 0) {
          newErrors[field] = 'Informe um valor válido';
        }
      });
    };

    if (hasCmdrs) {
      // Validate required fields
      validateRequiredFields({
        instrument,
        instrumentNumber,
        publicationDate,
        documentUuid,
        status,
        structure,
        presidingInstitution: presidingInstitution || mandateEnd,
      });

      // Validate positive numbers
      validatePositiveNumber({
        qtdPoderPublico,
        qtdSociedadeCivil,
      });

      // Special validation for presidingInstitution when mandateEnd is empty
      if (!presidingInstitution && !mandateEnd) {
        newErrors.presidingInstitution = 'Campo obrigatório';
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onNext();
    }
  };

  let hasCmdrsValue;

  if (hasCmdrs === null) {
    hasCmdrsValue = '';
  } else if (hasCmdrs) {
    hasCmdrsValue = 'sim';
  } else {
    hasCmdrsValue = 'nao';
  }






  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          O município possui CMDRS?
        </Typography>
        <RadioGroup
          row
          value={hasCmdrsValue}
          onChange={handleHasCmdrs}
          name="has-cmdrs"
        >
          <FormControlLabel value="sim" control={<Radio />} label="Sim" />
          <FormControlLabel value="nao" control={<Radio />} label="Não" />
        </RadioGroup>
      </Paper>

      {hasCmdrs && (
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Dados do CMDRS
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <FormControl fullWidth margin="dense" required>
                <InputLabel>Tipo do Instrumento/Dispositivo Legal</InputLabel>
                <Select
                  value={instrument}
                  onChange={e => setInstrument(e.target.value)}
                  error={!!errors.instrument}
                  label="Tipo do Instrumento/Dispositivo Legal"
                >
                  {instrumentTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
                {errors.instrument && <Typography color="error">{errors.instrument}</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={12}>
              <TextField
                label="Número do instrumento"
                type="number"
                inputProps={{ min: 0, pattern: '[0-9]*' }}
                value={instrumentNumber}
                onChange={e => setInstrumentNumber(e.target.value.replace(/\D/g, ''))}
                error={!!errors.instrumentNumber}
                helperText={errors.instrumentNumber}
                fullWidth
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <TextFieldDataCustom
                label="Data da publicação"
                value={publicationDate}
                onChange={(e: any) => setPublicationDate(e.target.value)}
                error={!!errors.publicationDate}
                helperText={errors.publicationDate}
                fullWidth
                margin="dense"
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <DocumentComponent
                uuid={documentUuid ?? null}
                setUuid={(uuid: string) => {
                  setDocumentUuid(uuid);
                }}
                podeSubstituir={true}
                podeEnviar={true}
                title='Faça o upload do documento comprobatório'
                subtitle='Para dar continuidade ao preenchimento, é necessário anexar o documento legal correspondente (como Decreto, Lei, Portaria ou outro normativo oficial).'

              />
              {errors.document_uuid && <Typography color="error">{errors.document_uuid}</Typography>}
            </Grid>
            <Grid item xs={12} md={12}>
              <FormControl fullWidth margin="dense" required>
                <InputLabel>Situação do CMDRS</InputLabel>
                <Select
                  value={status}
                  onChange={e => setStatus(e.target.value)}
                  error={!!errors.status}
                  label="Situação do CMDRS"
                >
                  <MenuItem value="ativo">Ativo e atuante</MenuItem>
                  <MenuItem value="inativo">Inativo</MenuItem>
                </Select>
                {errors.status && <Typography color="error">{errors.status}</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={12}>
              <FormControl fullWidth margin="dense" required>
                <InputLabel>Estrutura da diretoria</InputLabel>
                <Select
                  value={structure}
                  onChange={e => setStructure(e.target.value)}
                  error={!!errors.structure}
                  label="Estrutura da diretoria"
                >
                  <MenuItem value="fixo">Mandato fixo</MenuItem>
                  <MenuItem value="rotativo">Mandato rotativo</MenuItem>
                </Select>
                {errors.structure && <Typography color="error">{errors.structure}</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={12}>
              <TextField
                label="Nome da instituição que preside"
                value={presidingInstitution}
                onChange={e => setPresidingInstitution(e.target.value.toUpperCase())}
                fullWidth
                margin="dense"
                required={!mandateEnd}
              />

            </Grid>
            <Grid item xs={12} md={12}>
              <TextFieldDataCustom
                label="Data de término do mandato"
                value={mandateEnd}
                onChange={e => setMandateEnd(e.target.value)}
                fullWidth
                margin="dense"
                InputLabelProps={{ shrink: true }}
                required={!presidingInstitution}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Quantidade de instituições participantes
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={12}>
              <TextField
                label="Quantidade de instituições do Poder público"
                type="number"
                inputProps={{ min: 0 }}
                value={qtdPoderPublico === 0 ? '' : qtdPoderPublico}
                onChange={e => {
                  const value = e.target.value.replace(/^0+(?!$)/, '');
                  setQtdPoderPublico(value === '' ? 0 : Number(value));
                }}
                fullWidth
                margin="dense"
                error={!!errors.qtdPoderPublico}
                helperText={errors.qtdPoderPublico}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <TextField
                label="Quantidade de instituições da Sociedade civil"
                type="number"
                inputProps={{ min: 0 }}
                value={qtdSociedadeCivil === 0 ? '' : qtdSociedadeCivil}
                onChange={e => {
                  const value = e.target.value.replace(/^0+(?!$)/, '');
                  setQtdSociedadeCivil(value === '' ? 0 : Number(value));
                }}
                fullWidth
                margin="dense"
                error={!!errors.qtdSociedadeCivil}
                helperText={errors.qtdSociedadeCivil}
              />
            </Grid>
          </Grid>
          {errors.institutions && (
            <Typography color="error" sx={{ mb: 2 }}>
              {errors.institutions}
            </Typography>
          )}
        </Paper>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          size="large"
        >
          {hasCmdrs ? 'Salvar e continuar' : 'Salvar'}
        </Button>
      </Box>
    </Box>
  );
};

export default Step1;
